import React, { createContext, useContext, useEffect, useState } from "react";
import { notification } from "antd";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { store } from "../../store/store";
import { clearAppError } from "../../features/common/redux/globalActions";
import { useIsMobile } from "../../hooks/useIsMobile";
import { applyThemeBackground } from "../../utils";

export type NotificationType = "success" | "error" | "info" | "warning";
export type ThemeMode = "light" | "dark" | "system";

interface AppContextType {
  notify: (
    type: NotificationType,
    message: string,
    description?: string,
    duration?: number,
  ) => void;
  isOnline: boolean;
  isDarkMode: boolean;
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  destroyAllNotifications: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const THEME_KEY = "theme-mode";

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [api, contextHolder] = notification.useNotification();
  const isOnline = useOnlineStatus();
  const isMobile = useIsMobile();
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    return (localStorage.getItem(THEME_KEY) as ThemeMode) || "system";
  });
  const [isDarkMode, setIsDarkMode] = useState(false);


  useEffect(() => {
    const root = document.documentElement;
    const systemPrefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;

    const shouldUseDark =
      themeMode === "dark" || (themeMode === "system" && systemPrefersDark);

    root.classList.toggle("dark", shouldUseDark);
    setIsDarkMode(shouldUseDark);
  }, [themeMode]);

  useEffect(() => {
    applyThemeBackground();
  }, [isDarkMode])

  useEffect(() => {
    if (themeMode !== "system") return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      document.documentElement.classList.toggle("dark", media.matches);
      setIsDarkMode(media.matches);
    };

    media.addEventListener("change", handler);
    return () => media.removeEventListener("change", handler);
  }, [themeMode]);

  const setThemeMode = (mode: ThemeMode) => {
    localStorage.setItem(THEME_KEY, mode);
    setThemeModeState(mode);
  };

  const notify = (
    type: NotificationType,
    message: string,
    description?: string,
    duration: number = 10,
  ) => {
    const key = Date.now().toString();
    api[type]({
      key,
      message,
      description,
      placement: isMobile ? "top" : "bottomRight",
      duration: duration,
      showProgress: true,
      onClick: () => api.destroy(key),
      onClose: () => store.dispatch(clearAppError("detail")),
    });
  };

  const destroyAllNotifications = () => {
    api.destroy();
    store.dispatch(clearAppError("detail"));
  };

  return (
    <AppContext.Provider
      value={{
        notify,
        isOnline,
        isDarkMode,
        themeMode,
        setThemeMode,
        destroyAllNotifications,
      }}
    >
      {contextHolder}
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};
